/**
 * ReportResultView — Success screen: sticky action bar + iframe preview.
 */
import { useState } from 'react'
import { reports } from '@/api/client'
import CompareView from './CompareView'

interface ReportResultViewProps {
  jobId:          string
  meta?:          Record<string, unknown>
  onRegenerate:   () => void
  onReset:        () => void
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M6.5 2v7M3.5 7l3 3 3-3M2 11h9"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export default function ReportResultView({
  jobId, meta, onRegenerate, onReset,
}: ReportResultViewProps) {
  const title         = meta?.title as string | undefined
  const questionCount = meta?.question_count as number | undefined
  const warnings      = (meta?.warnings ?? []) as string[]
  const [showCompare, setShowCompare] = useState(false)

  const warnMsg = warnings.length > 0
    ? '⚠️ Some search API keys are expired or unavailable. The report may rely on estimated data only. Please contact your administrator.'
    : null

  return (
    <div className="animate-fade-up">
      {/* API key warning banner */}
      {warnMsg && (
        <div className="mx-5 mt-5 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <span className="flex-shrink-0 mt-0.5">⚠️</span>
          <span>{warnMsg}</span>
        </div>
      )}

      {/* Sticky action bar */}
      <div className="sticky top-14 z-30 glass-bar border-b border-slate-100 px-5 py-3">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto min-w-0">
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700
                             rounded-full text-xs font-medium border border-emerald-100 flex-shrink-0">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              Ready
            </span>
            {title && (
              <span className="text-sm text-slate-700 font-medium truncate" title={title}>{title}</span>
            )}
            {questionCount && (
              <span className="hidden sm:inline text-xs text-slate-400">
                · {questionCount} question{questionCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <a href={reports.previewUrl(jobId)} target="_blank" rel="noopener noreferrer" className="action-btn-ghost">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M5.5 2H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7.5M8 2h3v3M11 2 6 7"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Open
            </a>
            <a href={reports.htmlUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> HTML
            </a>
            <a href={reports.markdownUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> Markdown
            </a>
            <a href={reports.pdfUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> PDF
            </a>
            <a href={reports.excelUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> Excel
            </a>
            <button onClick={onReset} className="action-btn-ghost">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M9 2l2 2-7 7H2V9l7-7z"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Edit
            </button>
            <button
              onClick={() => setShowCompare(v => !v)}
              className={`action-btn-ghost ${showCompare ? 'bg-violet-50 text-violet-700 border-violet-200' : ''}`}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M2 6.5h9M7 4l2.5 2.5L7 9M6 4 3.5 6.5 6 9"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Compare
            </button>
            <button
              onClick={onRegenerate}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-navy-900 text-white
                         rounded-lg text-xs font-medium hover:bg-navy-800 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <path d="M1.5 5.5A4 4 0 1 1 5.5 9.5M1.5 3v2.5H4"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Regenerate
            </button>
          </div>
        </div>
      </div>

      {/* Report iframe preview */}
      <div className="max-w-6xl mx-auto px-5 pt-6 pb-16">
        <div className="bg-white rounded-2xl shadow-card overflow-hidden border border-slate-100">
          <iframe
            src={reports.previewUrl(jobId)}
            title="Report preview"
            className="report-iframe"
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      </div>

      {/* Compare panel */}
      {showCompare && (
        <CompareView jobId={jobId} onClose={() => setShowCompare(false)} />
      )}
    </div>
  )
}
